import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { useKeyboardHandler } from "./KeyboardEventContext";

// Component Imports
import InteractiveTableCell, {
	ActionsTableCell,
} from "./Tables/InteractiveTableCell";
import SelectTable from "./Tables/SelectTable";
import {
	nullsLastSort,
	ID_COLUMN_WIDTH,
	MAIN_COLUMN_WIDTH,
} from "./Tables/SortableTable";
import {
	defaultOrderBy,
	foldersOnTopOrderedBy,
} from "../utilities/tableSortUtils";
import Breadcrumbs from "./Breadcrumbs";
import {
	DocumentContextMenu,
	EmailContextMenu,
	FolderContextMenu,
	MatterNoteContextMenu,
} from "./FileList";
import { ButtonWithIcon, ButtonWithRouter } from "./ButtonWithIcon";
import { DropDownButtonWithRouter, DropDownButton } from "./DropDownButton";
import FileDropzone from "./FileDropzone";
import DocumentSlidingPane from "./DocumentPreviewRightPane";
import { withDocumentPreviewPane } from "./useDocumentPreviewPane";
import ButtonLink from "./ButtonLink";
import {
	iconMap,
	encodeFullpath,
	humanFileSize,
	shortDateFormat,
	filmCreditsFlexHelper,
	pluralizeWithNum,
} from "../utilities/utilities";

import { bulkUploadMultipleFiles } from "../actions/newDocumentUpload";

// Actions
import {
	getSignedUrlAndDownload,
	getSignedUrlForDocumentAndDownload,
	createNewFolder,
	changeFolderName,
	deleteDocuments,
	deleteFolders,
	restoreDocuments,
	restoreDocumentsFromArchive,
	restoreFoldersFromArchive,
	restoreNotesFromArchive,
	restoreEmailsFromArchive,
	archiveDocuments,
	archiveFolders,
	archiveEmails,
	archiveNotes,
	restoreFolders,
	setFavorite,
	setFavoriteFolder,
	deleteEmails,
	restoreEmails,
	deleteNotes,
	restoreNotes,
	accessCreateNote,
	setLockStatus,
	openDocumentInLauncher,
	showRenameDocumentDialog,
	convertDocumentToPDF,
	aiConversationAboutDocument,
	copyDocLinkToClipboard,
	handleDisabledKeyBoardShortcut,
} from "../actions/documentActions";
import {
	showNewFolderDialog,
	showEditFolderDialog,
} from "../actions/folderActions";
import {
	cut,
	copy,
	paste,
	duplicateDocument,
	selectAll,
	deleteMultiselect,
	restoreMultiselect,
	restoreFromArchiveMultiselect,
	setAllCheckboxes,
	downloadAllMultiselect,
	externalSendAllMultiselect,
	archiveMultiselect,
} from "../actions/multiselectActions";
import { exportMatter } from "../actions/matterActions";
import { testGetAllDocs } from "../actions/jobProgressActions";
import { requestOCR } from "../actions/ocrJobProgressActions";
import {
	uploadMultipleEmails,
	updateEmailNote,
	getSignedUrlForEmailAndDownload,
} from "../actions/emailActions";
import { updateUserSetting } from "../actions/userSettingsActions";
import {
	setDocumentListShowTrash,
	toggleDocumentListShowTrash,
	setDocumentListShowArchive,
	toggleDocumentListShowArchive,
} from "../actions/browserLocalstorageActions";
import { openGlobalDialog } from "../actions/globalEditDialogActions";
import { openGlobalSnackbar } from "../actions/globalSnackbarActions";
import { uploadNewVersionWithValidation } from "../actions/uploadNewVersionActions";
import {
	linkLXMatterWithClio,
	unlinkLXMatterfromClio,
	openLinkExistingMatterToClioDialog,
} from "../actions/clioIntegrationActions";
import { addDocumentToCompare } from "../actions/documentCompareActions";
import {
	selectTableRootMap,
	selectTableTypeMap,
} from "../reducers/multiselectReducer";
import {
	IconArrowUp,
	IconCirclePlus,
	IconEye,
	IconFileUpload,
	IconFolderPlus,
	IconFolderUp,
	IconLink,
	IconMailUp,
	IconSparkles,
	IconSquarePlus,
	IconSticker2,
	IconUpload,
} from "@tabler/icons-react";
import { LexWorkplaceGavelIcon } from "./LexWorkplaceIcons";
import {
	ARCHIVE_LIFECYCLE_STATUS,
	getDisabledStateFromLifecycleStatuses,
	NORMAL_LIFECYCLE_STATUS,
	TRASH_LIFECYCLE_STATUS,
} from "../utilities/lifecycleStatus";
import {
	EMAIL_UPLOAD_ACCEPT,
	EMAIL_DROPZONE_ACCEPT,
} from "../utilities/emailFileTypes";
import { selectArchiveMatterContentsAccess } from "../utilities/archiveAccess";

const rowLifecycleStatus = (row) =>
	row.documentLifecycleStatus ??
	row.emailLifecycleStatus ??
	row.matterNoteLifecycleStatus ??
	row.folderLifecycleStatus;

const copyPasteCompatibility = {
	"docs/": "documents",
	"public/": "documents",
	"private/": "documents",
	"email/": "email",
	"notes/": "notes",
};

const IdCell = (row) => {
	//note that under this implementation the folder is still filterable
	//via the ID filter text field.  Makes the results a bit confusing perhaps, but useful
	//if you are looking for a folder by ID
	return (
		<div style={{ textAlign: "left" }} data-id={row.value}>
			{row.original.type === "Folder" ? "" : row.value}
		</div>
	);
};

const NameCell = (props) => {
	const { iconComponent } = iconMap(props.row);
	return InteractiveTableCell({
		...props,
		iconComponent,
	});
};

import { tabUrlMap } from "./DocumentListUtils";

function DocumentListKeyboardHandler({ handleKeys, onKeyEvent }) {
	useKeyboardHandler(handleKeys, onKeyEvent);
	return null;
}

class DocumentList extends Component {
	constructor(props, context) {
		super(props, context);
		this.state = {
			filter: "All",
			itemsMatchingFilter: [],
			tableColumns: null,
			lastListDataWasAbbreviated: null,
			lastCurrentRoot: null,
		};
		this.clickedFile = this.clickedFile.bind(this);
		this.clickedEmail = this.clickedEmail.bind(this);
		this.bulkFileListChangeHandler =
			this.bulkFileListChangeHandler.bind(this);
		this.fileListChangeHandler = this.fileListChangeHandler.bind(this);
		this.handleRejectedFiles = this.handleRejectedFiles.bind(this);
		this.getFilteredDocuments = this.getFilteredDocuments.bind(this);
		this.toggleFoldersOnTop = this.toggleFoldersOnTop.bind(this);
		this.newButton = this.newButton.bind(this);
		this.folderCell = this.folderCell.bind(this);
		this.documentCell = this.documentCell.bind(this);
		this.documentActionsCell = this.documentActionsCell.bind(this);
		this.emailSubjectCell = this.emailSubjectCell.bind(this);
		this.emailActionsCell = this.emailActionsCell.bind(this);
		this.noteCell = this.noteCell.bind(this);
		this.noteActionsCell = this.noteActionsCell.bind(this);
		this.handleKeyboardShortcut = this.handleKeyboardShortcut.bind(this);
		this.headerBoxClicked = this.headerBoxClicked.bind(this);
		this.cut = this.cut.bind(this);
		this.copy = this.copy.bind(this);
		this.paste = this.paste.bind(this);
		this.duplicate = this.duplicate.bind(this);
		this.testGetAll = this.testGetAll.bind(this);
		this.renameDocumentHandler = this.renameDocumentHandler.bind(this);
		this.makeUploadsMenu = this.makeUploadsMenu.bind(this);
		this.makeActionsMenu = this.makeActionsMenu.bind(this);
		this.openEmailShortNoteDialog =
			this.openEmailShortNoteDialog.bind(this);
		this.emailShortNoteCell = this.emailShortNoteCell.bind(this);
		this.emailColumnConfig = [
			{
				Header: "ID",
				accessor: "id",
				width: ID_COLUMN_WIDTH,
				Cell: IdCell,
			},
			{
				Header: "Subject",
				accessor: "subjectline",
				width: MAIN_COLUMN_WIDTH,
				Cell: (row) =>
					row.original.type === "Folder"
						? this.folderCell(row)
						: this.emailSubjectCell(row),
				filterValueMethod: (row) =>
					row.type === "Folder" ? row.filename : row.subjectline,
				wildcard: true,
				unhidable: true,
			},
			{
				Header: "Short Note",
				accessor: "notes",
				wildcard: true,
				Cell: (row) => this.emailShortNoteCell(row),
			},
			{ Header: "From", accessor: "emailFrom", wildcard: true },
			{ Header: "To", accessor: "emailTo", wildcard: true },
			{
				Header: "Size",
				accessor: "filesize",
				filterable: false,
				Cell: (row) =>
					row.original.type === "Email"
						? humanFileSize(row.value, true)
						: "",
			},
			{
				Header: "Date",
				accessor: "date",
				Cell: (row) => (row.value ? shortDateFormat(row.value) : ""),
				filterValueMethod: (x) => shortDateFormat(x.date),
				sortingFn: nullsLastSort,
			},
			{ Header: "Attachments", accessor: "numAttachments", width: 100 },
			{
				Header: "",
				accessor: "actions",
				width: 110,
				sortable: false,
				filterable: false,
				Cell: (row) =>
					row.original.type === "Folder"
						? this.folderActionsCellNoFavorite(row)
						: this.emailActionsCell(row),
			},
		];
		this.notesColumnConfig = [
			{
				Header: "ID",
				accessor: "id",
				width: ID_COLUMN_WIDTH,
				Cell: IdCell,
			},
			{
				Header: "Name",
				accessor: "name",
				width: MAIN_COLUMN_WIDTH,
				Cell: (row) =>
					row.original.type === "Folder"
						? this.folderCell(row)
						: this.noteCell(row),
				filterValueMethod: (row) =>
					row.type === "Folder" ? row.filename : row.name,
				wildcard: true,
				unhidable: true,
			},
			{
				Header: "Created On",
				accessor: "createdOn",
				Cell: (row) => (row.value ? shortDateFormat(row.value) : ""),
				filterValueMethod: (x) => shortDateFormat(x.createdOn),
				sortingFn: nullsLastSort,
			},
			{ Header: "Created By", accessor: "createdBy", wildcard: true },
			{
				Header: "Modified On",
				accessor: "modifiedOn",
				Cell: (row) => (row.value ? shortDateFormat(row.value) : ""),
				filterValueMethod: (x) => shortDateFormat(x.modifiedOn),
				sortingFn: nullsLastSort,
			},
			{ Header: "Modified By", accessor: "modifiedBy", wildcard: true },
			{
				Header: "",
				accessor: "actions",
				width: 110,
				sortable: false,
				filterable: false,
				Cell: (row) =>
					row.original.type === "Folder"
						? this.folderActionsCellNoFavorite(row)
						: this.noteActionsCell(row),
			},
		];

		this.docsColumnConfig = [
			{
				Header: "ID",
				accessor: "id",
				width: ID_COLUMN_WIDTH,
				Cell: IdCell,
				sortable: true,
				filterable: true,
			},
			{
				Header: "Name",
				accessor: "filename",
				width: MAIN_COLUMN_WIDTH,
				Cell: (row) => {
					return (
						<div>
							{row.original.type === "Folder"
								? this.folderCell(row)
								: this.documentCell(row)}
						</div>
					);
				},
				unhidable: true,
				wildcard: true,
				sortable: true,
				filterable: true,
			},
			{
				Header: "Notes",
				accessor: "notes",
				width: 70,
				Cell: (row) => {
					return row.value ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								height: "100%",
								alignItems: "center",
							}}
							title={row.value}
						>
							<IconSticker2 size={16} fill="lightgrey" />
						</div>
					) : null;
				},
				sortable: true,
				filterable: true,
			},
			{
				Header: "Ver",
				accessor: "currentMajorVersion",
				width: 50,
				sortable: true,
				filterable: false,
			},
			{
				Header: "Size",
				accessor: "size",
				Cell: (row) =>
					row.original.type === "File"
						? humanFileSize(row.value, true)
						: "",
				sortable: true,
				filterable: false,
			},
			{
				Header: "Tags",
				accessor: "tags",
				Cell: (row) =>
					row.value
						? row.value.map((x) => (
								<div className="tagPill" key={x.name}>
									{x.name}
								</div>
							))
						: "",
				filterValueMethod: (x) =>
					(x.tags || []).map((t) => t.name).join(", ") || null,
				sortable: true,
				filterable: true,
			},
			{
				Header: "Document Type",
				accessor: "documentType",
				wildcard: true,
				sortable: true,
				filterable: true,
			},
			{
				Header: "Status",
				accessor: "documentStatus",
				wildcard: true,
				sortable: true,
				filterable: true,
			},
			{
				Header: "Locked By",
				accessor: "lockedBy",
				wildcard: true,
				sortable: true,
				filterable: true,
			},
			{
				Header: "Created On",
				accessor: "createdAt",
				Cell: (row) => (row.value ? shortDateFormat(row.value) : ""),
				filterValueMethod: (x) => shortDateFormat(x.createdAt),
				sortingFn: nullsLastSort,
				sortable: true,
				filterable: true,
			},
			{
				Header: "Last Modified",
				accessor: "lastModified",
				Cell: (row) => (row.value ? shortDateFormat(row.value) : ""),
				filterValueMethod: (x) => shortDateFormat(x.lastModified),
				sortingFn: nullsLastSort,
				sortable: true,
				filterable: true,
			},

			{
				Header: "",
				accessor: "actions",
				width: 170,
				Cell: (row) =>
					row.original.type === "Folder"
						? this.folderActionsCell(row)
						: this.documentActionsCell(row),
				sortable: false,
				filterable: false,
			},
		];

		tabUrlMap["docs/"].columns = this.docsColumnConfig;
		tabUrlMap["public/"].columns = this.docsColumnConfig;
		tabUrlMap["private/"].columns = this.docsColumnConfig;
		tabUrlMap["email/"].columns = this.emailColumnConfig;
		tabUrlMap["notes/"].columns = this.notesColumnConfig;

		this.keyboardShortcuts = {
			"ctrl+a": {
				allowedHandler: (e) => {
					this.headerBoxClicked();
					e.preventDefault();
					return false;
				},
				// allow select all because maybe they want to download all
				disabledHandler: (e) => {
					this.headerBoxClicked();
					e.preventDefault();
					return false;
				},
			},
			"ctrl+c": {
				allowedHandler: this.copy,
				disabledHandler: () =>
					this.props.handleDisabledKeyBoardShortcut({
						message: "Copy is not allowed here.",
					}),
			},
			"ctrl+x": {
				allowedHandler: this.cut,
				disabledHandler: () =>
					this.props.handleDisabledKeyBoardShortcut({
						message: "Cut is not allowed here.",
					}),
			},
			"ctrl+v": {
				allowedHandler: this.paste,
				disabledHandler: () =>
					this.props.handleDisabledKeyBoardShortcut({
						message: "Paste is not allowed here.",
					}),
			},
			"ctrl+n": {
				allowedHandler: (e) => {
					e.preventDefault();
					return false;
				},
			},
			"ctrl+u": { allowedHandler: () => {} },
			"meta+a": {
				allowedHandler: (e) => {
					this.headerBoxClicked();
					e.preventDefault();
					return false;
				},
				// allow select all because maybe they want to download all
				disabledHandler: (e) => {
					this.headerBoxClicked();
					e.preventDefault();
					return false;
				},
			},
			"meta+c": {
				allowedHandler: this.copy,
				disabledHandler: () =>
					this.props.handleDisabledKeyBoardShortcut({
						message: "Copy is not allowed here.",
					}),
			},
			"meta+x": {
				allowedHandler: this.cut,
				disabledHandler: () =>
					this.props.handleDisabledKeyBoardShortcut({
						message: "Cut is not allowed here.",
					}),
			},
			"meta+v": {
				allowedHandler: this.paste,
				disabledHandler: () =>
					this.props.handleDisabledKeyBoardShortcut({
						message: "Paste is not allowed here.",
					}),
			},
			"meta+n": {
				allowedHandler: (e) => {
					e.preventDefault();
					return false;
				},
			},
			"meta+u": { allowedHandler: () => {} },
			del: {
				allowedHandler: () => {
					attemptDelete(this.props);
				},
				disabledHandler: () =>
					this.props.handleDisabledKeyBoardShortcut({
						message:
							"Moving items to or from the Trash Bin is not permitted here.",
					}),
			},
		};
	}

	static getDerivedStateFromProps(nextProps, prevState) {
		if (
			prevState.lastListDataWasAbbreviated !==
				nextProps.documentListDataColumnsAreAbbreviated ||
			prevState.lastCurrentRoot !== nextProps.currentRoot
		) {
			// This implements a sort of class component "memoization"
			// of the table columns so that they are not re-calculated each render
			const tableColumns = DocumentList.calculateTableColumns(
				nextProps.currentRoot,
				nextProps.documentListDataColumnsAreAbbreviated
			);

			return {
				tableColumns,
				lastListDataWasAbbreviated:
					nextProps.documentListDataColumnsAreAbbreviated,
				lastCurrentRoot: nextProps.currentRoot,
			};
		}
		return null;
	}

	static calculateTableColumns(
		currentRoot,
		documentListDataColumnsAreAbbreviated
	) {
		return tabUrlMap[currentRoot].columns.map((col) => {
			if (documentListDataColumnsAreAbbreviated) {
				if (!["id", "filename"].includes(col.accessor)) {
					return { ...col, sortable: false, filterable: false };
				}
			}
			return col;
		});
	}

	isContainerInTrashOrArchive() {
		return (
			this.props.folderLifecycleStatus === TRASH_LIFECYCLE_STATUS ||
			this.props.matterLifecycleStatus === TRASH_LIFECYCLE_STATUS ||
			this.props.folderLifecycleStatus === ARCHIVE_LIFECYCLE_STATUS ||
			this.props.matterLifecycleStatus === ARCHIVE_LIFECYCLE_STATUS
		);
	}

	componentDidUpdate() {
		if (
			this.props.folderLifecycleStatus === TRASH_LIFECYCLE_STATUS ||
			this.props.matterLifecycleStatus === TRASH_LIFECYCLE_STATUS
		) {
			if (!this.props.documentListShowTrash) {
				this.props.setDocumentListShowTrash(true);
			}
		}
		if (
			this.props.folderLifecycleStatus === ARCHIVE_LIFECYCLE_STATUS ||
			this.props.matterLifecycleStatus === ARCHIVE_LIFECYCLE_STATUS
		) {
			if (!this.props.documentListShowArchive) {
				this.props.setDocumentListShowArchive(true);
			}
		}
	}
	handleKeyboardShortcut(key, event) {
		// Should we disable keyboard shortcuts if the user is in a location where we disable most actions (in trash folder/matter)?
		if (this.props.matterOrFolderLifecycleStatusObject.disabled) {
			return this.keyboardShortcuts[key].disabledHandler(event);
		}
		return this.keyboardShortcuts[key].allowedHandler(event);
	}
	setItemsMatchingFilter(itemsMatchingFilter) {
		if (
			itemsMatchingFilter.length != this.state.itemsMatchingFilter.length
		) {
			this.setState({ itemsMatchingFilter });
		}
	}
	headerBoxClicked() {
		var config = { Object: {}, Folder: {} /*, header: newChecked*/ };
		if (this.state.itemsMatchingFilter.length) {
			this.state.itemsMatchingFilter.forEach((item) => {
				let originalItem = item._original ? item._original : item;
				config[selectTableTypeMap[originalItem.type]][originalItem.id] =
					{
						...originalItem,
					};
			});
		} else {
			this.getFilteredDocuments().forEach((item) => {
				config[selectTableTypeMap[item.type]][item.id] = {
					...item,
				};
			});
		}

		this.props.setAllCheckboxes(
			selectTableRootMap[this.props.root],
			config
		);
	}
	folderCell(row) {
		const folderLifecycleStatusObject =
			getDisabledStateFromLifecycleStatuses({
				folderLifecycleStatus: row.original.folderLifecycleStatus,
			});
		return (
			<NameCell
				itemLifecycleStatusObject={folderLifecycleStatusObject}
				noStar
				row={row}
				trashClickHandler={() => {
					(folderLifecycleStatusObject.disabled
						? this.props.restoreFolders
						: this.props.deleteFolders
					).call(
						null,
						[row.original.id],
						this.props.matterID,

						this.props.apiBasepath
					);
				}}
				menuItems={FolderContextMenu({
					folder: {
						folderLifecycleStatus:
							row.original.folderLifecycleStatus,
					},
					permissions: {
						matterOrFolderLifecycleStatusObject:
							this.props.matterOrFolderLifecycleStatusObject,
						archiveAccess: this.props.archiveMatterContentsAccess,
					},
					callbacks: {
						onRename: () =>
							this.props.showEditFolderDialog({
								folderID: row.original.id,
								matterID: this.props.matterID,
								currentName: row.original.filename,
								apiBasepath: this.props.apiBasepath,
								currentLocationFilesAndFolders:
									this.props.documentList,
							}),
						onDelete: () =>
							this.props.deleteFolders(
								[row.original.id],
								this.props.matterID,
								this.props.apiBasepath
							),
						onRestore: () =>
							this.props.restoreFolders(
								[row.original.id],
								this.props.matterID,
								this.props.apiBasepath
							),
						onArchive: () =>
							this.props.archiveFolders(
								[row.original.id],
								this.props.matterID,
								this.props.apiBasepath
							),
						onUnarchive: () =>
							this.props.restoreFoldersFromArchive(
								[row.original.id],
								this.props.matterID,
								this.props.apiBasepath
							),
					},
				})}
			>
				<Link
					to={encodeFullpath(
						this.props.urlPrefix +
							((this.props.fullpath || "") +
								row.original.filename) +
							"/"
					)}
				>
					{row.original.filename}
				</Link>
			</NameCell>
		);
	}
	documentActionsCell(row) {
		const documentLifecycleStatusObject =
			getDisabledStateFromLifecycleStatuses({
				documentLifecycleStatus: row.original.documentLifecycleStatus,
			});
		return (
			<ActionsTableCell
				row={row}
				numColumns={5}
				itemLifecycleStatusObject={documentLifecycleStatusObject}
				isFavorite={row.original.isFavorite}
				isCheckedOut={
					row.original.lockStatus === 2 ||
					row.original.lockStatus === 3
				}
				isInUse={
					row.original.lockStatus === 1 ||
					row.original.lockStatus === 3
				}
				showCheckedOutSpace
				trashClickHandler={() => {
					(documentLifecycleStatusObject.IN_TRASH
						? this.props.restoreDocuments
						: this.props.deleteDocuments
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveClickHandler={() => {
					(documentLifecycleStatusObject.IN_ARCHIVE
						? this.props.restoreDocumentsFromArchive
						: this.props.archiveDocuments
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveDisabled={
					(documentLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabled
				}
				archiveDisabledHoverText={
					(documentLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabledText
				}
				matterOrFolderLifecycleStatusObject={
					this.props.matterOrFolderLifecycleStatusObject
				}
				disableFavorite={
					this.props.matterOrFolderLifecycleStatusObject.disabled
				}
				starClickHandler={() => {
					this.props.setFavorite(
						row.original.id,
						!row.original.isFavorite,
						this.props.matterID,
						this.props.apiBasepath
					);
				}}
			/>
		);
	}
	folderActionsCell(row) {
		const folderLifecycleStatusObject =
			getDisabledStateFromLifecycleStatuses({
				folderLifecycleStatus: row.original.folderLifecycleStatus,
			});
		return (
			<ActionsTableCell
				row={row}
				numColumns={5}
				itemLifecycleStatusObject={folderLifecycleStatusObject}
				isFavorite={row.original.isFavorite}
				trashClickHandler={() => {
					(folderLifecycleStatusObject.IN_TRASH
						? this.props.restoreFolders
						: this.props.deleteFolders
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveClickHandler={() => {
					(folderLifecycleStatusObject.IN_ARCHIVE
						? this.props.restoreFoldersFromArchive
						: this.props.archiveFolders
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveDisabled={
					(folderLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabled
				}
				archiveDisabledHoverText={
					(folderLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabledText
				}
				matterOrFolderLifecycleStatusObject={
					this.props.matterOrFolderLifecycleStatusObject
				}
				disableFavorite={
					this.props.matterOrFolderLifecycleStatusObject.disabled
				}
				starClickHandler={() => {
					this.props.setFavoriteFolder(
						row.original.id,
						!row.original.isFavorite,
						this.props.matterID,
						this.props.apiBasepath
					);
				}}
			/>
		);
	}
	folderActionsCellNoFavorite(row) {
		const folderLifecycleStatusObject =
			getDisabledStateFromLifecycleStatuses({
				folderLifecycleStatus: row.original.folderLifecycleStatus,
			});
		return (
			<ActionsTableCell
				row={row}
				numColumns={5}
				itemLifecycleStatusObject={folderLifecycleStatusObject}
				matterOrFolderLifecycleStatusObject={
					this.props.matterOrFolderLifecycleStatusObject
				}
				isFavorite={row.original.isFavorite}
				trashClickHandler={() => {
					(folderLifecycleStatusObject.IN_TRASH
						? this.props.restoreFolders
						: this.props.deleteFolders
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveClickHandler={() => {
					(folderLifecycleStatusObject.IN_ARCHIVE
						? this.props.restoreFoldersFromArchive
						: this.props.archiveFolders
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveDisabled={
					(folderLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabled
				}
				archiveDisabledHoverText={
					(folderLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabledText
				}
			/>
		);
	}
	emailActionsCell(row) {
		const emailLifecycleStatusObject =
			getDisabledStateFromLifecycleStatuses({
				emailLifecycleStatus: row.original.emailLifecycleStatus,
			});
		return (
			<ActionsTableCell
				row={row}
				numColumns={2}
				itemLifecycleStatusObject={emailLifecycleStatusObject}
				matterOrFolderLifecycleStatusObject={
					this.props.matterOrFolderLifecycleStatusObject
				}
				trashClickHandler={() => {
					(emailLifecycleStatusObject.IN_TRASH
						? this.props.restoreEmails
						: this.props.deleteEmails
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveClickHandler={() => {
					(emailLifecycleStatusObject.IN_ARCHIVE
						? this.props.restoreEmailsFromArchive
						: this.props.archiveEmails
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveDisabled={
					(emailLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabled
				}
				archiveDisabledHoverText={
					(emailLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabledText
				}
			/>
		);
	}
	noteActionsCell(row) {
		const noteLifecycleStatusObject = getDisabledStateFromLifecycleStatuses(
			{
				matterNoteLifecycleStatus:
					row.original.matterNoteLifecycleStatus,
			}
		);
		return (
			<ActionsTableCell
				row={row}
				numColumns={2}
				itemLifecycleStatusObject={noteLifecycleStatusObject}
				matterOrFolderLifecycleStatusObject={
					this.props.matterOrFolderLifecycleStatusObject
				}
				trashClickHandler={() => {
					(noteLifecycleStatusObject.IN_TRASH
						? this.props.restoreNotes
						: this.props.deleteNotes
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveClickHandler={() => {
					(noteLifecycleStatusObject.IN_ARCHIVE
						? this.props.restoreNotesFromArchive
						: this.props.archiveNotes
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				archiveDisabled={
					(noteLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabled
				}
				archiveDisabledHoverText={
					(noteLifecycleStatusObject.IN_ARCHIVE
						? this.props.archiveMatterContentsAccess.unarchive
						: this.props.archiveMatterContentsAccess.archive
					).disabledText
				}
			/>
		);
	}
	documentCell(row) {
		const documentLifecycleStatusObject =
			getDisabledStateFromLifecycleStatuses({
				matterLifecycleStatus: this.props.matterLifecycleStatus,
				folderLifecycleStatus: this.props.folderLifecycleStatus,
				documentLifecycleStatus: row.original.documentLifecycleStatus,
			});

		const documentActionsAreDisabled =
			documentLifecycleStatusObject.disabled;

		const docCanBeOpenedInLauncher =
			this.props.launcherInstalled &&
			(!row.original.lockedBySub ||
				row.original.lockedBySub === this.props.cognitoSub) &&
			!documentActionsAreDisabled;

		const menuItems = DocumentContextMenu({
			document: row.original,
			permissions: {
				matterOrFolderLifecycleStatusObject:
					this.props.matterOrFolderLifecycleStatusObject,
				archiveAccess: this.props.archiveMatterContentsAccess,
				cognitoSub: this.props.cognitoSub,
				launcherInstalled: this.props.launcherInstalled,
				canOverrideDocumentLocks: this.props.canOverrideDocumentLocks,
				isOcrEnabled: this.props.isOcrEnabled,
				isTenantOverOcrLimit: this.props.isTenantOverOcrLimit,
				isLiteraCompareVisible: this.props.isLiteraCompareVisible,
				isLiteraCompareEnabled: this.props.isLiteraCompareEnabled,
				isInlineCompareEnabled: this.props.isInlineCompareEnabled,
				docCompareType: this.props.docCompareType,
				firstDocIdInDocumentCompare:
					this.props.firstDocIdInDocumentCompare,
			},
			callbacks: {
				onDownload: this.props.getSignedUrlForDocumentAndDownload,
				onRename: this.renameDocumentHandler,
				onDuplicate: this.duplicate,
				onUploadNewVersion: this.props.uploadNewVersionWithValidation,
				onConvertToPDF: this.props.convertDocumentToPDF,
				onRequestOCR: this.props.requestOCR,
				onAddToLiteraCompare: this.props.addDocumentToCompare,
				onAddToInlineCompare: this.props.addDocumentToCompare,
				onSetLockStatus: this.props.setLockStatus,
				onCopyDocLink: this.props.copyDocLinkToClipboard,
				onDelete: this.props.deleteDocuments,
				onRestore: this.props.restoreDocuments,
				onArchive: this.props.archiveDocuments,
				onUnarchive: this.props.restoreDocumentsFromArchive,
			},
			location: {
				matterID: this.props.matterID,
				apiBasepath: this.props.apiBasepath,
				originalPrefix: this.props.originalPrefix,
			},
			userRights: this.props.userRights,
			isLexworkplaceAdmin: this.props.isLexworkplaceAdmin,
			tenantID: this.props.tenantID,
		});

		return (
			<NameCell
				row={row}
				menuItems={menuItems}
				otherButtons={[
					{
						iconComponent: <IconLink size={20} />,
						onClick: () => {
							this.props.copyDocLinkToClipboard(row.original.id);
						},
						title: "Copy Internal Document URL",
					},
					{
						iconComponent: (
							<IconSparkles fill="currentColor" size={20} />
						),
						onClick: () => {
							this.props.documentPreviewPane.openItemAI(
								row.original
							);
						},
						disabled: documentActionsAreDisabled,
						disabledHoverText:
							documentLifecycleStatusObject.disabledText,
						title: "Open AI for This Document",
					},
					{
						iconComponent: <IconEye size={20} />,
						title: "Preview This Document",
						onClick: () => {
							this.props.documentPreviewPane.openItemPreview({
								type: "File",
								id: row.original.id,
								title: row.original.filename,
								rowData: {
									...row.original,
									matterOrFolderLifecycleStatusObject:
										this.props
											.matterOrFolderLifecycleStatusObject,
								},
							});
						},
					},
				]}
			>
				{docCanBeOpenedInLauncher ? (
					<ButtonLink
						onClick={(e) => {
							this.clickedFile(row.original.id);
							e.preventDefault();
							return false;
						}}
						title={row.value}
					>
						{row.value}
					</ButtonLink>
				) : (
					<span title={row.value}>{row.value}</span>
				)}
			</NameCell>
		);
	}

	emailSubjectCell(row) {
		const emailLifecycleStatusObject =
			getDisabledStateFromLifecycleStatuses({
				emailLifecycleStatus: row.original.emailLifecycleStatus,
			});
		const menuItems = EmailContextMenu({
			email: row,
			permissions: {
				matterOrFolderLifecycleStatusObject:
					this.props.matterOrFolderLifecycleStatusObject,
				archiveAccess: this.props.archiveMatterContentsAccess,
			},
			callbacks: {
				onDownload: this.clickedEmail,
				onEditNote: this.openEmailShortNoteDialog,
				onDelete: this.props.deleteEmails,
				onRestore: this.props.restoreEmails,
				onArchive: this.props.archiveEmails,
				onUnarchive: this.props.restoreEmailsFromArchive,
			},
			location: {
				matterID: this.props.matterID,
				apiBasepath: this.props.apiBasepath,
				root: this.props.root,
			},
			isLexworkplaceAdmin: this.props.isLexworkplaceAdmin,
			tenantID: this.props.tenantID,
		});

		return (
			<NameCell
				row={row}
				itemLifecycleStatusObject={emailLifecycleStatusObject}
				noStar
				trashClickHandler={() => {
					(emailLifecycleStatusObject.disabled
						? this.props.restoreEmails
						: this.props.deleteEmails
					).call(
						null,
						[row.original.id],
						this.props.matterID,
						this.props.apiBasepath,
						this.props.root
					);
				}}
				menuItems={menuItems}
				otherButtons={[
					{
						iconComponent: <IconEye size={20} />,
						title: "Preview This Email",
						onClick: () => {
							this.props.documentPreviewPane.openItemPreview({
								type: "Email",
								id: row.original.id,
								title: row.original.subjectline,
								rowData: {
									...row.original,
									matterOrFolderLifecycleStatusObject:
										this.props
											.matterOrFolderLifecycleStatusObject,
								},
							});
						},
					},
				]}
			>
				<ButtonLink
					onClick={(e) => {
						this.clickedEmail(row.original.id);
						e.preventDefault();
						return false;
					}}
				>
					{row.original.subjectline}
				</ButtonLink>
			</NameCell>
		);
	}
	noteCell(row) {
		const noteLifecycleStatusObject = getDisabledStateFromLifecycleStatuses(
			{
				matterNoteLifecycleStatus:
					row.original.matterNoteLifecycleStatus,
			}
		);
		const menuItems = MatterNoteContextMenu({
			note: row.original,
			permissions: {
				matterOrFolderLifecycleStatusObject:
					this.props.matterOrFolderLifecycleStatusObject,
				archiveAccess: this.props.archiveMatterContentsAccess,
			},
			callbacks: {
				onDelete: this.props.deleteNotes,
				onRestore: this.props.restoreNotes,
				onArchive: this.props.archiveNotes,
				onUnarchive: this.props.restoreNotesFromArchive,
			},
			location: {
				matterID: this.props.matterID,
				apiBasepath: this.props.apiBasepath,
				root: this.props.root,
			},
			isLexworkplaceAdmin: this.props.isLexworkplaceAdmin,
			tenantID: this.props.tenantID,
		});

		return (
			<NameCell
				row={row}
				itemLifecycleStatusObject={noteLifecycleStatusObject}
				noStar
				menuItems={menuItems}
				otherButtons={[
					{
						iconComponent: <IconEye size={20} />,
						title: "Preview This Note",
						onClick: () => {
							this.props.documentPreviewPane.openItemPreview({
								type: "Note",
								id: row.original.id,
								title: row.original.name,
								rowData: {
									...row.original,
									matterOrFolderLifecycleStatusObject:
										this.props
											.matterOrFolderLifecycleStatusObject,
								},
							});
						},
					},
				]}
			>
				<Link to={"/notes/" + row.original.id}>
					{row.original.name}
				</Link>
			</NameCell>
		);
	}
	newButton() {
		const map = {
			docs: () => (
				<DropDownButton
					title="Upload..."
					iconComponent={
						<IconUpload
							size={16}
							style={{ verticalAlign: "text-bottom" }}
						/>
					}
					pullRight
					menuItems={this.makeUploadsMenu()}
					disabled={
						this.props.matterOrFolderLifecycleStatusObject.disabled
					}
				/>
			),
			email: () => (
				<ButtonWithIcon
					fileListChangeHandler={this.fileListChangeHandler}
					buttonText="Upload Email"
					iconComponent={
						<IconMailUp
							size={16}
							style={{ verticalAlign: "text-bottom" }}
						/>
					}
					type="multi-upload"
					uploadAccept={EMAIL_UPLOAD_ACCEPT}
					disabled={
						this.props.matterOrFolderLifecycleStatusObject.disabled
					}
				/>
			),
			notes: () => (
				<ButtonWithRouter
					redirect={
						"/notes/new/" +
						this.props.matterID +
						"/" +
						this.props.directoryID +
						"/notes/" +
						(this.props.fullpath || "")
					}
					handler={this.props.accessCreateNote}
					buttonText="Create Note"
					iconComponent={
						<IconCirclePlus
							size={16}
							style={{ verticalAlign: "text-bottom" }}
						/>
					}
					disabled={
						this.props.matterOrFolderLifecycleStatusObject.disabled
					}
				/>
			),
		};
		map.public = map.docs;
		map.private = map.docs;
		return map[this.props.root.replace("/", "")]();
	}

	createNoteHandler() {
		this.props.accessCreateNote(
			this.props.matterID,
			this.props.directoryID,
			this.props.fullpath
		);
	}
	clickedFile(documentID) {
		this.props.openDocumentInLauncher(documentID, this.props.cognitoSub);
	}
	clickedEmail(emailID) {
		this.props.getSignedUrlForEmailAndDownload(emailID);
	}
	cut() {
		if (this.props.cutCopyAllowed) {
			this.props.cut(this.props.apiBasepath, this.props.directoryID);
		}
	}
	copy() {
		if (this.props.cutCopyAllowed) {
			this.props.copy(this.props.apiBasepath, this.props.directoryID);
		}
	}
	paste() {
		if (this.props.pasteAllowed) {
			this.props.paste({
				matterID: this.props.matterID,
				destinationDirectoryID: this.props.directoryID,
				destinationDirectoryPath: this.props.apiBasepath,
				backendRoot: tabUrlMap[this.props.root].backendRoot, //documents, email, notes
				autoRename: false, //auto rename.  False means user will be prompted about duplicates
			});
		}
	}
	duplicate(thisDocument) {
		this.props.duplicateDocument({
			matterID: this.props.matterID,
			destinationDirectoryID: this.props.directoryID,
			destinationDirectoryPath: this.props.apiBasepath,
			backendRoot: tabUrlMap[this.props.root].backendRoot, //documents, email, notes
			autoRename: true, // auto rename by default
			documentObject: thisDocument,
		});
	}
	testGetAll() {
		this.props.testGetAllDocs();
	}

	getFilteredDocuments() {
		return this.props.documentList.filter((row) => {
			if (
				!this.props.documentListShowTrash &&
				rowLifecycleStatus(row) === TRASH_LIFECYCLE_STATUS
			) {
				// filter out docs in trash unless the user has chosen to show them
				return false;
			}
			if (
				!this.props.documentListShowArchive &&
				rowLifecycleStatus(row) === ARCHIVE_LIFECYCLE_STATUS
			) {
				// filter out docs in archive unless the user has chosen to show them
				return false;
			}
			return true;
		});
	}

	toggleFoldersOnTop() {
		this.props.updateUserSetting("foldersOnTop", !this.props.foldersOnTop);
	}

	bulkFileListChangeHandler(files, callback) {
		if (this.props.root !== "email/") {
			this.props.bulkUploadMultipleFiles({
				matterID: this.props.matterID,
				basepath: this.props.apiBasepath,
				directoryID: this.props.directoryID,
				files,
				autoRename: false, //autoRename parameter.  False on first call
				callback,
			});
		} else {
			this.props.uploadMultipleEmails(
				this.props.matterID,
				this.props.apiBasepath,
				this.props.directoryID,
				files,
				false,
				callback
			);
		}
	}
	handleRejectedFiles() {
		if (this.props.root === "email/") {
			this.props.openGlobalSnackbar({
				message: "Only email files can be uploaded to the Emails tab",
				variant: "warning",
			});
		}
	}
	fileListChangeHandler(files, callback) {
		if (this.props.root !== "email/") {
			this.props.bulkUploadMultipleFiles({
				matterID: this.props.matterID,
				basepath: this.props.apiBasepath,
				directoryID: this.props.directoryID,
				files,
				autoRename: false, //autoRename parameter.  False on first call
				callback,
			});
		} else {
			this.props.uploadMultipleEmails(
				this.props.matterID,
				this.props.apiBasepath,
				this.props.directoryID,
				files,
				false,
				callback
			);
		}
	}

	renameDocumentHandler(documentID, currentName) {
		this.props.showRenameDocumentDialog({
			documentID,
			currentName,
			matterID: this.props.matterID,
			apiBasepath: this.props.apiBasepath,
			currentLocationFilesAndFolders: this.props.documentList,
		});
	}

	getCsvFileName() {
		switch (this.props.currentRoot) {
			case "email/":
				return "emails";
			case "notes/":
				return "matter-notes";
			default:
				return "documents";
		}
	}

	makeActionsMenu(openCsvExport) {
		var menuItems = [
			{
				type: "label",
				text: (
					<>
						{`Trash toggle moved to Show `}
						<IconArrowUp
							size={14}
							style={{ verticalAlign: "sub" }}
						/>
					</>
				),
			},
			{
				type: "label",
				text: (
					<>
						{`Folder toggle moved to Show `}
						<IconArrowUp
							size={14}
							style={{ verticalAlign: "sub" }}
						/>
					</>
				),
			},
			{ divider: true },
			{
				// always allow select all. They may want to download everything in a trashed context
				text: filmCreditsFlexHelper("Select All", "(CTRL + A)"),
				onSelect: this.headerBoxClicked,
			},
			{
				text: filmCreditsFlexHelper("Cut", "(CTRL + X)"),
				onSelect: this.cut,
				disabled:
					!this.props.cutCopyAllowed ||
					this.props.matterOrFolderLifecycleStatusObject.disabled,
				disabledHoverText: this.props
					.matterOrFolderLifecycleStatusObject.disabled
					? this.props.matterOrFolderLifecycleStatusObject
							.disabledText
					: "",
			},
			{
				text: filmCreditsFlexHelper("Copy", "(CTRL + C)"),
				onSelect: this.copy,
				disabled:
					!this.props.cutCopyAllowed ||
					this.props.matterOrFolderLifecycleStatusObject.disabled,
				disabledHoverText: this.props
					.matterOrFolderLifecycleStatusObject.disabled
					? this.props.matterOrFolderLifecycleStatusObject
							.disabledText
					: "",
			},
			{
				text: filmCreditsFlexHelper("Paste", "(CTRL + V)"),
				onSelect: this.paste,
				disabled:
					!this.props.pasteAllowed ||
					this.props.matterOrFolderLifecycleStatusObject.disabled,
				disabledHoverText: this.props
					.matterOrFolderLifecycleStatusObject.disabled
					? this.props.matterOrFolderLifecycleStatusObject
							.disabledText
					: "",
			},
			{ divider: true },
			this.props.selectedItems ? getDownloadAllMenuItem(this.props) : {},
			this.props.selectedItems ? getExternalSendMenuItem(this.props) : {},
		];
		if (
			// not for firm docs
			// and clio integration is enabled
			this.props.matterID > 0 &&
			this.props.clioIntegrationSettingStatus
		) {
			menuItems = menuItems.concat([{ divider: true }]);

			if (this.props.clioMatterID === undefined) {
				menuItems.push({
					text: "Link to Matter in Clio Manage",
					onSelect: () =>
						this.props.openLinkExistingMatterToClioDialog(
							this.props.matterID
						),
					disabled: this.props.matterLifecycleStatus !== "normal",
				});
			} else {
				menuItems.push({
					text: "Un-link Matter from Clio Manage",
					onSelect: () =>
						this.props.unlinkLXMatterfromClio(
							this.props.clioMatterID,
							this.props.matterID
						),
					disabled: this.props.matterLifecycleStatus !== "normal",
				});
			}
		}
		if (this.props.matterID > 0) {
			menuItems = menuItems.concat([
				{ divider: true },
				{
					text: "Export Matter",
					onSelect: () =>
						this.props.exportMatter(this.props.matterID),
				},
			]);
		}
		menuItems = menuItems.concat([
			{ divider: true },
			{
				text: "Export Data as CSV",
				onSelect: openCsvExport,
			},
		]);
		menuItems = menuItems.concat(getDeleteMenuItem(this.props));
		return menuItems;
	}

	makeUploadsMenu() {
		var menuItems = [
			{
				text: "Upload File(s)",
				type: "multi-file-upload",
				iconComponent: (
					<IconFileUpload
						size={16}
						style={{ verticalAlign: "text-bottom" }}
					/>
				),
				fileListChangeHandler: this.bulkFileListChangeHandler,
				disabled:
					this.props.matterOrFolderLifecycleStatusObject.disabled,
				disabledHoverText:
					this.props.matterOrFolderLifecycleStatusObject.disabledText,
			},
			{
				text: "Upload Folder",
				type: "multi-folder-upload",
				iconComponent: (
					<IconFolderUp
						size={16}
						style={{ verticalAlign: "text-bottom" }}
					/>
				),
				fileListChangeHandler: this.bulkFileListChangeHandler,
				disabled:
					this.props.matterOrFolderLifecycleStatusObject.disabled,
				disabledHoverText:
					this.props.matterOrFolderLifecycleStatusObject.disabledText,
			},
		];
		return menuItems;
	}

	openEmailShortNoteDialog(row) {
		this.props.openGlobalDialog(
			{
				label: row.original.notes
					? "Edit Email Short Note"
					: "Add Email Short Note",
				value: row.original.notes,
				submitButtonLabel: "Update Note",
			},
			(state) => {
				this.props.updateEmailNote({
					emailID: row.original.id,
					value: state.value,
				});
			},
			(state) => {
				return {
					canSubmit: state.value !== row.original.notes,
					erroMessage: "",
				};
			}
		);
	}
	emailShortNoteCell(row) {
		const emailShortNoteDisabledState =
			getDisabledStateFromLifecycleStatuses({
				matterLifecycleStatus: this.props.matterLifecycleStatus,
				folderLifecycleStatus: this.props.folderLifecycleStatus,
				emailLifecycleStatus: row.original.emailLifecycleStatus,
			});
		return row.original.type === "Folder" ? (
			<div>{row.value || ""}</div>
		) : (
			<InteractiveTableCell
				row={row}
				otherButtons={[
					{
						iconComponent: <IconSquarePlus size={20} />,
						onClick: () => {
							this.openEmailShortNoteDialog(row);
						},
						disabled: emailShortNoteDisabledState.disabled,
						disabledHoverText:
							emailShortNoteDisabledState.disabledText ?? "",
						title: row.original.notes
							? "Edit Email Short Note"
							: "Add Email Short Note",
					},
				]}
			>
				{row.value || ""}
			</InteractiveTableCell>
		);
	}
	render() {
		return (
			<div className="fullHeightFlex">
				<DocumentListKeyboardHandler
					handleKeys={Object.keys(this.keyboardShortcuts)}
					onKeyEvent={this.handleKeyboardShortcut}
				/>

				<FileDropzone
					fileHandler={this.bulkFileListChangeHandler}
					enabled={this.props.root !== "notes/"}
					accept={
						this.props.root === "email/"
							? EMAIL_DROPZONE_ACCEPT
							: undefined
					}
					onFilesRejected={this.handleRejectedFiles}
				>
					<SelectTable
						className="fullWidth"
						showTrash={this.props.documentListShowTrash}
						onToggleShowTrash={
							this.props.toggleDocumentListShowTrash
						}
						showTrashToggleDisabled={this.isContainerInTrashOrArchive()}
						showArchive={this.props.documentListShowArchive}
						onToggleShowArchive={
							this.props.toggleDocumentListShowArchive
						}
						showArchiveToggleDisabled={this.isContainerInTrashOrArchive()}
						foldersOnTop={this.props.foldersOnTop}
						onToggleFoldersOnTop={this.toggleFoldersOnTop}
						actionButtons={({ openCsvExport }) => (
							<div style={{ display: "flex", gap: 8 }}>
								<ButtonWithIcon
									handler={() =>
										this.props.showNewFolderDialog({
											matterID: this.props.matterID,
											apiBasepath: this.props.apiBasepath,
											currentLocationFilesAndFolders:
												this.props.documentList,
										})
									}
									disabled={
										this.props
											.matterOrFolderLifecycleStatusObject
											.disabled
									}
									buttonText="New Folder"
									iconComponent={
										<IconFolderPlus
											size={16}
											style={{ verticalAlign: "middle" }}
										/>
									}
								/>
								{this.newButton()}
								<DropDownButtonWithRouter
									title="Actions"
									iconComponent={<LexWorkplaceGavelIcon />}
									pullRight
									menuItems={this.makeActionsMenu(
										openCsvExport
									)}
								/>
							</div>
						)}
						leftSideFilterSpace={
							<div
								style={{
									display: "flex",
									marginTop: 10,
									marginRight: 12,
									marginBottom: 10,
									alignItems: "center",
									maxWidth: "40rem",
									marginLeft: 18,
								}}
							>
								<Breadcrumbs
									fullpath={this.props.fullpath}
									basepath={this.props.urlPrefix}
									basepathname={this.props.breadcrumbRootName}
									directoryHierarchy={
										this.props.directoryHierarchy
									}
								/>
							</div>
						}
						data={
							this.props.loading
								? []
								: this.props.documentList &&
									this.getFilteredDocuments()
						}
						csvExport={{
							extraColumns: [
								{
									Header: "Is Folder",
									accessor: (row) =>
										row.type === "Folder" ? "Yes" : "",
								},
							],
							csvColumnRenderers: {
								tags: (val) =>
									(val || []).map((t) => t.name).join("; "),
								size: (val) =>
									val ? humanFileSize(val, true) : "",
								createdAt: (val) =>
									val ? shortDateFormat(val) : "",
								lastModified: (val) =>
									val ? shortDateFormat(val) : "",
								filesize: (val) =>
									val ? humanFileSize(val, true) : "",
								date: (val) =>
									val ? shortDateFormat(val) : "",
								createdOn: (val) =>
									val ? shortDateFormat(val) : "",
								modifiedOn: (val) =>
									val ? shortDateFormat(val) : "",
							},
							fileName: this.getCsvFileName(),
						}}
						autoResize
						root={this.props.root}
						columns={this.state.tableColumns}
						headerBoxClicked={this.headerBoxClicked}
						loading={this.props.loading}
						loadingMessage={this.props.loadingMessage}
						noResultsText={`No ${
							tabUrlMap[this.props.currentRoot].itemNoun
						} or folders here`}
						filterable
						defaultSorted={[{ id: "filename" }]}
						onSortedChange={(newSorted) => {
							newSorted.filter((sort) => sort.id === "filename");
						}}
						orderByMethod={
							this.props.foldersOnTop
								? foldersOnTopOrderedBy
								: defaultOrderBy
						}
						tableUniqueID={this.props.tableUniqueID}
						onFilteredChange={(filteredColumns) => {
							if (!filteredColumns || !filteredColumns.length) {
								this.setItemsMatchingFilter([]);
							}
						}}
						filteringCompleteCallback={(
							filteredData,
							filterId,
							activeFilters
						) => {
							if (activeFilters.length === 0) {
								this.setItemsMatchingFilter([]);
							} else {
								const lastActiveFilterId =
									activeFilters.slice(-1)[0].id;

								if (activeFilters.length === 1) {
									this.setItemsMatchingFilter(
										filteredData,
										lastActiveFilterId
									);
								} else if (lastActiveFilterId === filterId) {
									this.setItemsMatchingFilter(
										filteredData,
										lastActiveFilterId
									);
								}
							}
						}}
					/>
				</FileDropzone>
				<DocumentSlidingPane
					openPane={this.props.documentPreviewPane.previewOpen}
					selectedRowData={
						this.props.documentPreviewPane.previewSelectedRow ?? {}
					}
					closeRightPane={this.props.documentPreviewPane.closePreview}
					directoryPath={this.props.apiBasepath}
					aiMode={this.props.documentPreviewPane.aiMode}
					matterID={this.props.matterID}
				/>
			</div>
		);
	}
}
const attemptDelete = (props) => {
	const { selectedItems, matterID, apiBasepath, deleteMultiselect } = props;
	const count =
		Object.keys(selectedItems.Object).length +
		Object.keys(selectedItems.Folder).length;
	if (count > 0) {
		deleteMultiselect(selectedItems, matterID, apiBasepath);
	}
};
const getDeleteMenuItem = (props) => {
	let trashItemCount = 0;
	let archiveItemCount = 0;
	let normalItemCount = 0;
	const {
		selectedItems,
		matterID,
		apiBasepath,
		deleteAllowed,
		deleteMultiselect,
		restoreMultiselect,
		restoreFromArchiveMultiselect,
		archiveMultiselect,
	} = props;

	if (selectedItems) {
		Object.keys(selectedItems.Object).forEach((key) => {
			const status = rowLifecycleStatus(selectedItems.Object[key]);
			if (status === TRASH_LIFECYCLE_STATUS) trashItemCount++;
			else if (status === ARCHIVE_LIFECYCLE_STATUS) archiveItemCount++;
			else if (status === NORMAL_LIFECYCLE_STATUS) normalItemCount++;
		});
		Object.keys(selectedItems.Folder).forEach((key) => {
			const status = rowLifecycleStatus(selectedItems.Folder[key]);
			if (status === TRASH_LIFECYCLE_STATUS) trashItemCount++;
			else if (status === ARCHIVE_LIFECYCLE_STATUS) archiveItemCount++;
			else if (status === NORMAL_LIFECYCLE_STATUS) normalItemCount++;
		});
	}

	const itemsNotInArchive = normalItemCount + trashItemCount;
	const itemsNotInTrash = normalItemCount + archiveItemCount;
	const itemsNotNormal = trashItemCount + archiveItemCount;

	const { archive: archiveAccess, unarchive: unarchiveAccess } =
		props.archiveMatterContentsAccess;
	const baseDisabled =
		!deleteAllowed || props.matterOrFolderLifecycleStatusObject.disabled;
	// Moving to archive requires the firm feature and the user right
	const archiveDisabledProps = {
		disabled: baseDisabled || archiveAccess.disabled,
		disabledHoverText: archiveAccess.disabled
			? archiveAccess.disabledText
			: undefined,
	};
	// Moving archived items out of the archive (restore or trash) requires only the user right
	const leavingArchiveBlocked =
		archiveItemCount > 0 && unarchiveAccess.disabled;
	const leavingArchiveDisabledProps = {
		disabled: baseDisabled || leavingArchiveBlocked,
		disabledHoverText: leavingArchiveBlocked
			? unarchiveAccess.disabledText
			: undefined,
	};

	let menuItems = [];
	if (trashItemCount > 0 || archiveItemCount > 0) {
		let verb = "Restore";
		if (archiveItemCount > 0 && trashItemCount === 0) {
			verb = "Unarchive";
		}
		if (trashItemCount > 0 && archiveItemCount === 0) {
			verb = "Restore from Trash Bin";
		}
		// archive-only selections use the archive wording for the progress job and snackbar
		const restoreFn =
			archiveItemCount > 0 && trashItemCount === 0
				? restoreFromArchiveMultiselect
				: restoreMultiselect;
		menuItems.push({
			text: filmCreditsFlexHelper(`${verb} (${itemsNotNormal})`),
			onSelect: () => restoreFn(selectedItems, matterID, apiBasepath),
			...leavingArchiveDisabledProps,
		});
	}
	if (normalItemCount === 0 && archiveItemCount > 0) {
		menuItems.push({
			text: filmCreditsFlexHelper(
				`Move to Trash Bin (${itemsNotInTrash})`,
				"(DEL)"
			),
			onSelect: () =>
				deleteMultiselect(selectedItems, matterID, apiBasepath),
			...leavingArchiveDisabledProps,
		});
	}
	if (normalItemCount === 0 && trashItemCount > 0) {
		menuItems.push({
			text: filmCreditsFlexHelper(`Archive (${itemsNotInArchive})`),
			onSelect: () =>
				archiveMultiselect(selectedItems, matterID, apiBasepath),
			...archiveDisabledProps,
		});
	}
	if (normalItemCount > 0) {
		menuItems.push({
			text: filmCreditsFlexHelper(
				`Move to Trash Bin (${itemsNotInTrash})`,
				"(DEL)"
			),
			onSelect: () =>
				deleteMultiselect(selectedItems, matterID, apiBasepath),
			...leavingArchiveDisabledProps,
		});
		menuItems.push({
			text: filmCreditsFlexHelper(`Archive (${itemsNotInArchive})`),
			onSelect: () =>
				archiveMultiselect(selectedItems, matterID, apiBasepath),
			...archiveDisabledProps,
		});
	}
	if (menuItems.length > 0) {
		// add a divider before the delete menu items
		menuItems.unshift({ divider: true });
	}

	return menuItems;
};

const getExternalSendMenuItem = (props) => {
	const {
		selectedItems,
		matterID,
		apiBasepath,
		currentRoot,
		externalSendAllMultiselect,
	} = props;

	const selectedObjects = Object.keys(selectedItems.Object).length;
	const selectedFolders = Object.keys(selectedItems.Folder).length;

	const count = selectedObjects + selectedFolders;

	const allowedSendLocations = {
		"docs/": "Document",
		"public/": "Document",
		"private/": "Document",
		"email/": false,
		"notes/": false,
	};
	const sendAllowed = allowedSendLocations[currentRoot];
	const disabled =
		!sendAllowed ||
		count === 0 ||
		props.matterOrFolderLifecycleStatusObject.disabled ||
		props.userRights.CAN_SHARE_DOCUMENTS_EXTERNALLY !== true;
	const disabledHoverText = !sendAllowed
		? "External sending is not allowed for items in this location"
		: props.userRights.CAN_SHARE_DOCUMENTS_EXTERNALLY !== true
			? "You do not have permissions to share items externally. Please contact your firm administrator for more information."
			: props.matterOrFolderLifecycleStatusObject.disabled
				? props.matterOrFolderLifecycleStatusObject.disabledText
				: "";
	var menuNoun = "Document";
	if (selectedObjects === 0) {
		menuNoun = "Folder";
	}
	if (selectedObjects > 0 && selectedFolders > 0) {
		menuNoun = "Item";
	}

	const text =
		"Send " +
		(count > 0 && sendAllowed ? pluralizeWithNum(menuNoun, count) : "");

	const validateEmails = (recipients) => {
		var allValid = true;
		recipients &&
			recipients.forEach((email, key) => {
				if (!isEmail(email.value)) {
					allValid = false;
					recipients.splice(key, 1);
				}
			});

		return { emailsValidated: allValid, recipients: recipients };
	};
	const isEmail = (email) => {
		return /[\w\d.-]+@[\w\d.-]+\.[\w\d.-]+/.test(email);
	};
	const onSelect = () => {
		props.openGlobalDialog(
			{
				label: "Send " + menuNoun + (count && count > 1 ? "s" : ""),
				recipients: [],
				submitButtonLabel: "Send",
				type: "email",
				submitIcon: "send",
				mainText:
					"Enter recipient email addresses & and optional message to be included in the email.",
				canSubmit: false,
				passwordProtectShare: props.passwordProtectShare,
				extraCSSClass: "ExtraWideEditDialog",
			},
			(state) => {
				//Submit
				const recipients = state.recipients.map((x) => x.value);
				const message = state.textAreaContent;
				const passwordProtectShare = state.passwordProtectShare;

				externalSendAllMultiselect(
					selectedItems,
					`/${apiBasepath}`,
					matterID,
					recipients,
					message,
					passwordProtectShare
				);
			},
			(state) => {
				//VALIDATE
				const validateResult = validateEmails(state.recipients);

				return {
					canSubmit:
						validateResult &&
						validateResult.emailsValidated &&
						validateResult.recipients &&
						validateResult.recipients.length,
					errorMessage:
						!validateResult.recipients ||
						!validateResult.recipients.length
							? "At least one valid email is required"
							: !validateResult.emailsValidated
								? "All emails must be valid"
								: "",
					recipients: validateResult.recipients,
				};
			}
		);
	};
	return { text, onSelect, disabled, disabledHoverText };
};

const getDownloadAllMenuItem = (props) => {
	const {
		selectedItems,
		matterID,
		apiBasepath,
		downloadAllMultiselect,
		currentRoot,
		getSignedUrlAndDownload,
		getSignedUrlForDocumentAndDownload,
	} = props;

	const selectedObjectsCount = Object.keys(selectedItems.Object).length;
	const selectedFoldersCount = Object.keys(selectedItems.Folder).length;

	const count = selectedObjectsCount + selectedFoldersCount;

	const allowedDownloadLocations = {
		"docs/": "Document",
		"public/": "Document",
		"private/": "Document",
		"email/": "Email",
		"notes/": false,
	};

	const documentOrEmail = allowedDownloadLocations[currentRoot];

	var menuNoun = documentOrEmail;
	if (selectedObjectsCount === 0) {
		menuNoun = "Folder";
	}
	if (selectedObjectsCount > 0 && selectedFoldersCount > 0) {
		menuNoun = "Item";
	}

	const multiDownloadAllowed = count && documentOrEmail;

	const text =
		"Download " +
		(multiDownloadAllowed ? pluralizeWithNum(menuNoun, count) : "");

	const onSelect = () => {
		if (selectedObjectsCount === 1 && selectedFoldersCount === 0) {
			if (documentOrEmail === "Email") {
				var prefix =
					selectedItems.Object[Object.keys(selectedItems.Object)[0]]
						.prefix;
				getSignedUrlAndDownload(prefix, "email");
			} else if (documentOrEmail === "Document") {
				var selectedDocumentID =
					selectedItems.Object[Object.keys(selectedItems.Object)[0]]
						.id;
				getSignedUrlForDocumentAndDownload(selectedDocumentID);
			}
		} else {
			const objectTypesToInclude = [documentOrEmail];
			downloadAllMultiselect(
				selectedItems,
				objectTypesToInclude,
				`/${apiBasepath}`,
				matterID
			);
		}
	};

	const disabled = !multiDownloadAllowed;
	return { text, onSelect, disabled };
};

const mapStateToProps = (state) => {
	const currentSelection =
		state.multiselect[
			tabUrlMap[state.document.currentRoot].selectionListName
		];
	const selectionMap = {
		"docs/": "Object",
		"email/": "Object",
		"notes/": "Object",
		"public/": "Object",
		"private/": "Object",
	};
	const cutCopyAllowed =
		Object.keys(currentSelection[selectionMap[state.document.currentRoot]])
			.length +
			Object.keys(currentSelection.Folder).length >
		0;
	const pasteAllowed =
		Object.keys(state.multiselect.clipboard).length > 0 &&
		copyPasteCompatibility[
			state.multiselect.sourceBasepath.split("/")[0] + "/"
		] === copyPasteCompatibility[state.document.currentRoot] &&
		(state.multiselect.sourceDirectoryID !== state.document.directoryID ||
			!state.multiselect.cut); //not allowed to paste from a cut in the same directory
	const selectionName = selectTableRootMap[state.document.currentRoot];

	const tableUniqueID =
		tabUrlMap[state.document.currentRoot].customColumnsKey;

	return {
		currentPathname: state.globalSearchbar.location,
		documentList: state.document.documentList,
		prefix: state.document.prefix,
		directoryID: state.document.directoryID,
		myUserID: state.user.userID,
		loading: state.document.loading,
		loadingMessage: state.document.message,
		clipboard: state.multiselect.clipboard,
		selectedItems: state.multiselect[selectionName],
		currentRoot: state.document.currentRoot,
		pasteAllowed,
		currentSelection,
		cutCopyAllowed,
		deleteAllowed: cutCopyAllowed,
		cognitoSub: state.user.cognitoUser.attributes.sub,
		launcherInstalled: state.global.launcherInstalled,
		canOverrideDocumentLocks:
			state.user.userRights.CAN_OVERRIDE_DOCUMENT_LOCK === true,
		foldersOnTop: state.userSettings.foldersOnTop,
		originalPrefix: state.document.originalPrefix,
		isOcrEnabled: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantFeatures.automaticOCR === 1
			: true,
		isTenantOverOcrLimit: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantUsage.OCR_TRANSACTIONS_THIS_MONTH >=
				state.tenant.tenantLimits.OCR_PER_MONTH_NET
			: false,
		tableUniqueID,
		isLiteraCompareEnabled: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantFeatures.literaDocumentCompareEnabled === 1
			: true,
		isLiteraCompareVisible: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantFeatures.literaDocumentCompareVisible === 1
			: true,
		isInlineCompareEnabled: !state.tenant.tenantFeaturesLoading
			? state.tenant.tenantFeatures.inlineDocumentCompareEnabled === 1
			: true,
		firstDocIdInDocumentCompare: state.documentCompare.doc1ID,
		docCompareType: state.documentCompare.type,
		folderLifecycleStatus: state.document.folderLifecycleStatus,
		matterLifecycleStatus: state.document.matterLifecycleStatus,
		documentListDataColumnsAreAbbreviated:
			state.document.documentListDataColumnsAreAbbreviated,
		directoryHierarchy: state.document.directoryHierarchy,
		matterOrFolderLifecycleStatusObject:
			getDisabledStateFromLifecycleStatuses({
				matterLifecycleStatus: state.document.matterLifecycleStatus,
				folderLifecycleStatus: state.document.folderLifecycleStatus,
			}),
		userRights: state.user.userRights,
		isLexworkplaceAdmin:
			state.user.cognitoUser.cognitoGroups?.includes(
				"LexWorkplace_Admin"
			) ?? false,
		tenantID: state.user.cognitoUser.attributes?.["custom:tenantID"],
		passwordProtectShare: state.browserLocalstorage.passwordProtectShare,
		documentListShowTrash: state.browserLocalstorage.documentListShowTrash,
		documentListShowArchive:
			state.browserLocalstorage.documentListShowArchive,
		archiveMatterContentsAccess: selectArchiveMatterContentsAccess(state),
	};
};
export default connect(mapStateToProps, {
	getSignedUrlAndDownload,
	getSignedUrlForDocumentAndDownload,
	openGlobalDialog,
	openGlobalSnackbar,
	createNewFolder,
	bulkUploadMultipleFiles,
	changeFolderName,
	deleteDocuments,
	deleteFolders,
	restoreDocuments,
	restoreDocumentsFromArchive,
	restoreFoldersFromArchive,
	restoreNotesFromArchive,
	restoreEmailsFromArchive,
	archiveDocuments,
	archiveFolders,
	archiveEmails,
	archiveNotes,
	restoreFolders,
	uploadMultipleEmails,
	setFavorite,
	setFavoriteFolder,
	cut,
	copy,
	paste,
	duplicateDocument,
	selectAll,
	setAllCheckboxes,
	deleteMultiselect,
	restoreMultiselect,
	restoreFromArchiveMultiselect,
	archiveMultiselect,
	deleteEmails,
	restoreEmails,
	deleteNotes,
	restoreNotes,
	accessCreateNote,
	setLockStatus,
	openDocumentInLauncher,
	updateEmailNote,
	testGetAllDocs,
	showRenameDocumentDialog,
	downloadAllMultiselect,
	externalSendAllMultiselect,
	exportMatter,
	getSignedUrlForEmailAndDownload,
	linkLXMatterWithClio,
	unlinkLXMatterfromClio,
	openLinkExistingMatterToClioDialog,
	updateUserSetting,
	convertDocumentToPDF,
	requestOCR,
	showNewFolderDialog,
	showEditFolderDialog,
	aiConversationAboutDocument,
	addDocumentToCompare,
	copyDocLinkToClipboard,
	handleDisabledKeyBoardShortcut,
	uploadNewVersionWithValidation,
	setDocumentListShowTrash,
	toggleDocumentListShowTrash,
	setDocumentListShowArchive,
	toggleDocumentListShowArchive,
})(withDocumentPreviewPane(DocumentList));
